/**
 * Calculator Formatting Utilities
 *
 * Shared formatting functions for consistent display of
 * currency, percentages, and license plates.
 */

/**
 * Format a number as EUR currency (whole euros, no decimals)
 * Uses Dutch locale for proper thousands separator
 */
export function formatEuro(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a percentage value
 * Shows 1 decimal place max, removes trailing zeros
 */
export function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  // Round to 1 decimal, remove trailing .0
  const formatted = value.toFixed(1).replace(/\.0$/, '');
  return `${formatted}%`;
}

/**
 * Format a Dutch license plate with dashes (XX-999-XX format)
 * Handles various input formats and normalizes them
 */
export function formatPlate(plate: string): string {
  // Remove all non-alphanumeric characters and uppercase
  const clean = plate.replace(/[^A-Z0-9]/gi, '').toUpperCase();

  // Dutch plates are 6 characters
  if (clean.length === 6) {
    // Standard format: XX-99-XX or 99-XX-XX or XX-XX-99 etc.
    return `${clean.slice(0, 2)}-${clean.slice(2, 4)}-${clean.slice(4, 6)}`;
  }

  // Return as-is if not standard length
  return clean;
}

/**
 * Format a visual plate display while typing
 * Shows dashes as user types for better readability
 */
export function formatPlateDisplay(input: string): string {
  // Remove all non-alphanumeric and uppercase
  const clean = input.replace(/[^A-Z0-9]/gi, '').toUpperCase();

  // Add dashes progressively as user types
  if (clean.length <= 2) {
    return clean;
  } else if (clean.length <= 4) {
    return `${clean.slice(0, 2)}-${clean.slice(2)}`;
  } else {
    return `${clean.slice(0, 2)}-${clean.slice(2, 4)}-${clean.slice(4, 6)}`;
  }
}

/**
 * Normalize a license plate input for API calls
 * Accepts various formats and returns clean uppercase
 */
export function normalizePlateInput(input: string): string {
  return input.replace(/[^A-Z0-9]/gi, '').toUpperCase();
}

/**
 * Format a date/time for display in copy output
 */
export function formatDateTime(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}
